/**
 * FoodWasteResults
 *
 * Design: Sustainability-focused with cost/impact visualization
 * - Environmental impact hero section
 * - Category breakdown with progress bars
 * - Detailed item grid with food icons
 */
import React from "react";
import { FoodWasteResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  Scale,
  DollarSign,
  Apple,
  Leaf,
  PieChart,
  List,
  TrendingDown,
  AlertTriangle,
} from "lucide-react";

interface FoodWasteResultsProps {
  results: ResultsType;
}

export const FoodWasteResults: React.FC<FoodWasteResultsProps> = ({
  results,
}) => {
  const isVideo = results.type === "video";
  const itemsCount = isVideo
    ? results.total_items_detected || 0
    : results.items_detected || 0;

  // Calculate totals for percentage
  const categoryTotal = Object.values(results.category_breakdown_kg).reduce(
    (a, b) => a + b,
    0
  );
  const foodTypeTotal = Object.values(results.food_type_breakdown_kg).reduce(
    (a, b) => a + b,
    0
  );

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      grains: "bg-amber-500",
      vegetables: "bg-emerald-500",
      fruits: "bg-orange-500",
      proteins: "bg-red-500",
      dairy: "bg-blue-500",
      other: "bg-slate-500",
    };
    return colors[category.toLowerCase()] || "bg-primary-500";
  };

  return (
    <div className="space-y-6">
      {/* Impact Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Waste */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/20 via-slate-800/50 to-slate-900 border border-amber-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Scale className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white">
              {results.total_waste_grams}g
            </p>
            <p className="text-slate-400 text-sm mt-1">Total Waste Weight</p>
            <p className="text-amber-400 text-xs mt-2">
              {(results.total_waste_grams / 1000).toFixed(2)} kg
            </p>
          </div>
        </div>

        {/* Estimated Cost */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500/20 via-slate-800/50 to-slate-900 border border-red-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-400" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white">
              ${results.estimated_cost_usd.toFixed(2)}
            </p>
            <p className="text-slate-400 text-sm mt-1">Estimated Cost</p>
            <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Potential savings
            </p>
          </div>
        </div>

        {/* Items Detected */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/20 via-slate-800/50 to-slate-900 border border-emerald-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Apple className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white">
              {formatNumber(itemsCount)}
            </p>
            <p className="text-slate-400 text-sm mt-1">Items Detected</p>
            <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1">
              <Leaf className="w-3 h-3" />
              Food waste items
            </p>
          </div>
        </div>
      </div>

      {/* Category Breakdown with Progress Bars */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
            <PieChart className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Category Breakdown</h3>
            <p className="text-slate-400 text-sm">Waste by food category</p>
          </div>
        </div>

        <div className="space-y-4">
          {Object.entries(results.category_breakdown_kg)
            .sort(([, a], [, b]) => b - a)
            .map(([category, weight]) => {
              const percentage =
                categoryTotal > 0 ? (weight / categoryTotal) * 100 : 0;
              return (
                <div key={category}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium capitalize">
                      {category}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">
                        {percentage.toFixed(0)}%
                      </span>
                      <span className="text-amber-400 font-semibold">
                        {weight.toFixed(2)} kg
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getCategoryColor(
                        category
                      )} rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Food Type Breakdown */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <List className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Food Types</h3>
            <p className="text-slate-400 text-sm">
              Breakdown by specific food items
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(results.food_type_breakdown_kg)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 12)
            .map(([food, weight]) => (
              <div
                key={food}
                className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <p className="text-white font-medium capitalize text-sm mb-1">
                  {food.replace(/_/g, " ")}
                </p>
                <p className="text-emerald-400 font-bold">
                  {weight.toFixed(2)} kg
                </p>
                <p className="text-slate-500 text-xs">
                  {(weight * 1000).toFixed(0)}g
                </p>
              </div>
            ))}
        </div>
      </div>

      {/* Detected Items Detail */}
      {results.detected_items && results.detected_items.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Detected Items</h3>
                <p className="text-slate-400 text-sm">
                  {results.detected_items.length} items identified
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.detected_items.slice(0, 8).map((item, idx) => (
              <div
                key={idx}
                className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <Apple className="w-5 h-5 text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium capitalize">
                        {item.food_name.replace(/_/g, " ")}
                      </p>
                      <p className="text-slate-400 text-sm">{item.category}</p>
                    </div>
                    <span className="text-emerald-400 text-sm font-semibold">
                      {(item.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-amber-400">{item.weight_grams}g</span>
                    <span className="text-slate-500">
                      ({item.weight_kg.toFixed(3)}kg)
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
